import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { CrossCircleIcon } from "../../components/Icons";
import { S3ProviderListOutputItem } from "@aws-amplify/storage";
import { formatDateTime } from "../../utils/DateUtils";

type ModalProps = {
    showModal: boolean;
    setShowModale: (bool: boolean) => void;
    selectedFile: S3ProviderListOutputItem | undefined;
    handleDelete: (file: S3ProviderListOutputItem) => void;
    isProcessing: boolean;
    errorMessage: string;
};

/**
 * S3のファイル削除確認モーダル
 *
 * @param ModalProps
 * @returns JSX.Element
 */
const S3FileDeleteConfirmModal: React.FC<ModalProps> = ({
    showModal,
    setShowModale,
    selectedFile,
    handleDelete,
    isProcessing,
    errorMessage,
}) => {
    const { t } = useTranslation();

    return (
        <Modal
            className="fixed left-1/2 top-1/2 w-10/12 max-w-md -translate-x-1/2 -translate-y-1/2 transform whitespace-pre-wrap break-words rounded-lg border bg-white p-4 shadow-md"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            isOpen={showModal}
        >
            <div className="m-0 px-4 py-2 sm:m-2">
                <div className="text-lg font-bold">{t("DataManagement.deleteFile")}</div>
                <hr className="my-2 border-t-2"></hr>

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center py-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                <div className="m-0 grid grid-cols-2 items-center justify-center text-gray-900">
                    {/* ファイル名 */}
                    <div className="col-span-2 sm:col-span-1">
                        <label>{t("CommonLabel.fileName")}</label>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <label>{selectedFile?.key?.split("/").slice(2).join("/")}</label>
                    </div>
                    {/* 最終更新日時 */}
                    <div className="col-span-2 pt-3 sm:col-span-1 sm:pt-0">
                        <label>{t("CommonLabel.lastModified")}</label>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <label>{selectedFile?.lastModified ? formatDateTime(selectedFile?.lastModified) : ""}</label>
                    </div>
                    {/* サイズ */}
                    <div className="col-span-2 pt-3 sm:col-span-1 sm:pt-0">
                        <label>{t("CommonLabel.dataSize")}</label>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                        <label>
                            {selectedFile?.size &&
                                (selectedFile?.size < 1024 * 1024 ? (
                                    <span>{Number((selectedFile?.size / 1024).toFixed(1)).toLocaleString()} KB</span>
                                ) : (
                                    <span>
                                        {Number((selectedFile?.size / (1024 * 1024)).toFixed(1)).toLocaleString()} MB
                                    </span>
                                ))}
                        </label>
                    </div>
                </div>

                {/* キャンセル/削除ボタン */}
                <div className="mt-4 flex items-center justify-evenly">
                    <button className="btn-primary w-1/3" onClick={() => setShowModale(false)}>
                        {t("CommonLabel.cancel")}
                    </button>
                    <button
                        onClick={() => selectedFile && handleDelete(selectedFile)}
                        className={`w-1/3 rounded-lg  py-2.5 text-center font-medium text-gray-50 ${
                            isProcessing
                                ? "bg-red-900"
                                : "bg-red-600 hover:bg-red-800 active:bg-red-900 active:font-bold"
                        }  `}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <div className="flex items-center justify-center">
                                <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                <label className="">Processing...</label>
                            </div>
                        ) : (
                            t("CommonLabel.delete")
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default S3FileDeleteConfirmModal;
